import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { EGButton } from "../../../../../MainComponents/Button/EGButton/EGButton";
import { MeasuresForm } from "./MeasuresForm/MeasuresForm";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useParams } from "react-router";
import {
  createInvestigation,
  getCompensations,
  getMeasures,
  getSanctions,
} from "../../../../../../apis";
import { Loading } from "../../../../../Loading/String/Loading";
import { TakenActions } from "./TakenActions/TakenActions";
import { SanctionCompensationForm } from "./SanctionCompensationsForm/SanctionCompensationForm";
import { Confirmation } from "../../../../../Modal/Confirmation/Confirmation";

export const SanctionsAndResolutions = ({
  investigationList,
  reportDetail,
  reportRender,
  offenders,
  victims,
  setActiveTab,
  setIsLoading,
  isLoading,
}) => {
  // Custom Hooks
  const [t] = useTranslation("report");
  const { id } = useParams();
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const investigationNameRef = useRef("");

  // Initial State
  const [activeForm, setActiveForm] = useState(null);
  const [measures, setMeasures] = useState([]);
  const [sanctions, setSanctions] = useState([]);

  const [investigationName, setInvestigationName] = useState("");
  const [loadingInvestigation, setLoadingInvestigation] = useState(false);
  const [compensations, setCompensations] = useState([]);
  // Show modal confirmation
  const [modalConfirm, setModalConfirm] = useState({
    show: false,
    variant: "",
    title: "",
    titleColor: "",
    description1: "",
    description2: "",
    question: "",
    loading: false,
    // Left Button
    leftButtonText: "",
    leftButtonColor: "",
    leftButtonVariant: "contained",
    leftButtonLabelColor: "",
    leftIconName: "",
    leftIconColor: "",
    leftIconColorHover: "",
    onClickLeft: () => {},
    // Rigth Button
    rightButtonText: "",
    rightButtonColor: "",
    rightButtonVariant: "contained",
    rightButtonLabelColor: "",
    rightIconName: "",
    rightIconColor: "",
    rightIconColorHover: "",
    onClickRight: () => {},
    ethiquin: "",
    hasInvDescription: false,
  });
  // Open the confirmation modal with the information to be rendered
  const openModalConfirm = ({
    //key = // value by defualt
    variant = "light",
    title = "",
    titleColor = "secondary",
    description1 = "",
    description2 = "",
    question = "",
    loading = false,
    leftButtonText = "Cancel",
    leftButtonColor = "secondary",
    leftButtonVariant = "contained",
    leftButtonLabelColor = "",
    leftIconName = "",
    leftIconColor = "",
    leftIconColorHover = "",
    onClickLeft = () => {},
    rightButtonText = "Confirm",
    rightButtonColor = "primary",
    rightButtonVariant = "contained",
    rightButtonLabelColor = "",
    rightIconName = "",
    rightIconColor = "",
    rightIconColorHover = "",
    onClickRight = () => {},
    ethiquin = "",
    inputLabel = "",
    hasInvDescription = false,
  }) => {
    setModalConfirm({
      show: true,
      variant,
      title,
      titleColor,
      description1,
      description2,
      question,
      loading,
      leftButtonText,
      leftButtonColor,
      leftButtonVariant,
      leftButtonLabelColor,
      leftIconName,
      leftIconColor,
      leftIconColorHover,
      onClickLeft,
      rightButtonText,
      rightButtonColor,
      rightButtonVariant,
      rightButtonLabelColor,
      rightIconName,
      rightIconColor,
      rightIconColorHover,
      onClickRight,
      ethiquin,
      inputLabel,
      hasInvDescription,
    });
  };
  // Close confirmation modal
  const closeModalConfirm = () => {
    setModalConfirm((prevState) => ({
      ...prevState,
      show: false,
    }));
    setInvestigationName("");
  };
  // Enter investigation name
  const setInvName = (value) => {
    investigationNameRef.current = value;
    setInvestigationName(value);
  };
  // Get info from DB
  useEffect(() => {
    getAllMeasures();
    getAllSanctions();
    getAllCompensations();
  }, []);
  // Registered measures
  const getAllMeasures = async () => {
    try {
      await getMeasures(id, "all").then((resp) => {
        setMeasures(resp);
      });
    } catch (error) {
      console.error(error);
    }
  };
  // Sanctions registered
  const getAllSanctions = async () => {
    try {
      await getSanctions(id, "all").then((resp) => {
        setSanctions(resp);
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getAllCompensations = async () => {
    try {
      await getCompensations(id, "all").then((resp) => {
        setCompensations(resp);
      });
    } catch (error) {
      console.error(error);
    }
  };
  // The form selected is enabled
  const hdlEnableForm = (formIndex) => {
    setActiveForm(formIndex);
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };
  // Close form
  const closeForm = () => {
    setActiveForm(null);
  };
  // Create aditional investigation
  const additionalInv = async (type) => {
    const dataToSave = {
      id,
      type,
      title: investigationNameRef.current,
    };
    setLoadingInvestigation(true);

    const sending = enqueueSnackbar(`${t("Care.processing_request")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await createInvestigation(dataToSave);
      reportRender();
      closeModalConfirm();
      setInvestigationName("");
      investigationNameRef.current = "";
      enqueueSnackbar(t("Care.investigation_successfully_created"), {
        variant: "success",
      });
      setActiveTab("Investigación");
    } catch (error) {
      console.error(error);
    } finally {
      closeSnackbar(sending);
      setLoadingInvestigation(false);
    }
  };
  // If no investigation has been created
  const createNewInvestigation = () => {
    openModalConfirm({
      variant: "info",
      ethiquin: "inspector",
      title: t("Care.create_investigation"),
      inputLabel: t("Care.investigation_name"),
      description1: t(
        "Resolution.no_individuals_involved_have_been_identified_in_report"
      ),
      description2: t(
        "Resolution.to_proceed_sanction_you_must_first_conduct_investigation_identify_potential_parties_responsible"
      ),
      question: t("Care.what_kind_investigation_want_create"),
      hasInvDescription: true,
      leftButtonColor: "primary",
      leftButtonText: t("Care.advanced"),
      leftIconName: "search_setting_bold",
      leftIconColor: "white",
      leftIconColorHover: "white",
      onClickLeft: () => additionalInv("advance"),
      rightButtonColor: "success",
      rightButtonText: t("Care.basic"),
      rightButtonLabelColor: "white",
      rightIconName: "outline_search",
      rightIconColor: "white",
      rightIconColorHover: "white",
      onClickRight: () => additionalInv("simple"),
    });
  };
  // If there are no registered parties involved
  const goToInvestigations = () => {
    openModalConfirm({
      variant: "warning",
      title: t("Care.warning"),
      titleColor: theme.palette.warning.main,
      description1: t(
        "Resolution.no_individuals_involved_have_been_identified_in_report"
      ),
      description2: t(
        "Resolution.to_proceed_sanction_responsable_individuals_must_first_be_identified_through_investigation"
      ),
      question: t("Care.do_you_want_continue"),
      leftButtonText: t("Care.cancel"),
      leftButtonColor: "secondary",
      onClickLeft: () => closeModalConfirm(),
      rightButtonText: t("Resolution.go_to_investigation"),
      rightButtonColor: "primary",
      onClickRight: () => {
        setActiveTab("Investigación");
        closeModalConfirm();
      },
      ethiquin: "nodata",
    });
  };

  // Determining the available involved parties
  const getAvaiableInvolvedParty = (involved = [], resolutions = []) =>
    involved
      ?.filter(
        (person) =>
          !resolutions.some((resolution) => resolution.name === person.name)
      )
      .map((involved) => ({
        label: involved.name,
        value: involved.name,
        data: { ...involved },
        key: involved.name,
      }));

  const getAvaiableOffenders = getAvaiableInvolvedParty(offenders, sanctions);
  const getAvaiableVictims = getAvaiableInvolvedParty(victims, compensations);

  return (
    <>
      <Confirmation
        show={modalConfirm.show}
        variant={modalConfirm.variant}
        onHide={closeModalConfirm}
        title={modalConfirm.title}
        titleColor={modalConfirm.titleColor}
        description1={modalConfirm.description1}
        description2={modalConfirm.description2}
        question={modalConfirm.question}
        loading={isLoading || loadingInvestigation}
        // Left button action
        leftButtonText={modalConfirm.leftButtonText}
        leftButtonColor={modalConfirm.leftButtonColor}
        leftButtonVariant={modalConfirm.leftButtonVariant}
        leftButtonLabelColor={modalConfirm.leftButtonLabelColor}
        leftIconName={modalConfirm.leftIconName}
        leftIconColor={modalConfirm.leftIconColor}
        leftIconColorHover={modalConfirm.leftIconColorHover}
        onClickLeft={modalConfirm.onClickLeft}
        // Right button action
        rightButtonText={modalConfirm.rightButtonText}
        rightButtonColor={modalConfirm.rightButtonColor}
        rightButtonVariant={modalConfirm.rightButtonVariant}
        rightButtonLabelColor={modalConfirm.rightButtonLabelColor}
        rightIconName={modalConfirm.rightIconName}
        rightIconColor={modalConfirm.rightIconColor}
        rightIconColorHover={modalConfirm.rightIconColorHover}
        onClickRight={modalConfirm.onClickRight}
        ethiquin={modalConfirm.ethiquin}
        inputLabel={modalConfirm.inputLabel}
        inputValue={investigationName}
        setInputValue={(value) => setInvName(value)}
        hasInvDescription={modalConfirm.hasInvDescription}
      />
      <Box>
        <Stack
          display="flex"
          alignItems="stretch"
          justifyContent="space-around"
          flexDirection="row"
          flexWrap={{ xs: "wrap", sm: "nowrap" }}
          spacing={1}
          py={1}
        >
          {/* Open form buttons */}
          <EGButton
            label={t("Resolution.add_measure")}
            iconName="outline_ruler"
            disabled={activeForm !== null && activeForm !== "measures"}
            onClick={() => hdlEnableForm("measures")}
          />
          <EGButton
            label={t("Resolution.add_sanction")}
            iconName="judge_hammer"
            disabled={activeForm !== null && activeForm !== "sanctions"}
            onClick={() => {
              getAvaiableOffenders.length >= 1
                ? hdlEnableForm("sanctions")
                : investigationList.length >= 1
                ? goToInvestigations()
                : createNewInvestigation();
            }}
          />
          <EGButton
            label={t("Resolution.add_compensation")}
            iconName="outline_profile_add"
            disabled={activeForm !== null && activeForm !== "compensations"}
            onClick={() => {
              getAvaiableVictims.length >= 1
                ? hdlEnableForm("compensations")
                : investigationList.length >= 1
                ? goToInvestigations()
                : createNewInvestigation();
            }}
          />
        </Stack>
        {/* Forms */}
        {activeForm === "measures" && (
          <MeasuresForm
            closeForm={closeForm}
            reportRender={reportRender}
            getAllMeasures={getAllMeasures}
          />
        )}
        {activeForm === "sanctions" && (
          <SanctionCompensationForm
            closeForm={closeForm}
            reloadInfo={getAllSanctions}
            type={"offenders"}
            people={getAvaiableOffenders}
            reportRender={reportRender}
          />
        )}
        {activeForm === "compensations" && (
          <SanctionCompensationForm
            closeForm={closeForm}
            reloadInfo={getAllCompensations}
            type={"victims"}
            people={getAvaiableVictims}
            reportRender={reportRender}
          />
        )}

        {/* Actions and sanctions taken */}
        {!activeForm && (
          <TakenActions
            measures={measures}
            getAllMeasures={getAllMeasures}
            sanctions={sanctions}
            getAllSanctions={getAllSanctions}
            compensations={compensations}
            getAllCompensations={getAllCompensations}
            reportRender={reportRender}
            openModalConfirm={openModalConfirm}
            closeModalConfirm={closeModalConfirm}
            setIsLoading={setIsLoading}
          />
        )}
      </Box>
    </>
  );
};
